import {
    Badge,
    Box,
    Button,
    CloseButton,
    Flex,
    HStack,
    Icon,
    Input,
    InputGroup,
    InputLeftElement,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Spinner,
    Text,
    useDisclosure,
} from "@chakra-ui/react";
import { IconBuilding, IconChevronDown, IconChevronRight, IconFolder, IconSearch, IconSitemap } from "@tabler/icons-react";
import { useMemo, useRef, useState } from "react";
import { sortBy } from "lodash";
import type { EntityResponse } from "../../api/models/EntityResponse";

interface EntityScopePickerProps {
    entities: EntityResponse[] | undefined;
    selectedId: string | undefined;
    isLoading: boolean;
    onChange: (id: string | undefined) => void;
}

function findEntityById(entities: EntityResponse[], id: string): EntityResponse | undefined {
    for (const entity of entities) {
        if (entity.id === id) return entity;
        if (entity.children?.length) {
            const found = findEntityById(entity.children, id);
            if (found) return found;
        }
    }
    return undefined;
}

function matchesSearch(entity: EntityResponse, term: string): boolean {
    if (!term) return true;
    if (entity.name.toLowerCase().includes(term.toLowerCase())) return true;
    return (entity.children ?? []).some(c => matchesSearch(c, term));
}

interface TreeItemProps {
    entity: EntityResponse;
    searchTerm: string;
    depth: number;
    selectedId: string | undefined;
    onSelect: (id: string) => void;
}

function TreeItem({ entity, searchTerm, depth, selectedId, onSelect }: TreeItemProps) {
    const isDirectory = entity.entityType === "Directory";
    const hasChildren = isDirectory && entity.children.length > 0;
    const isSelected = entity.id === selectedId;

    const shouldForceExpand = searchTerm.length > 0 && isDirectory && entity.children.some(c => matchesSearch(c, searchTerm));
    const [isOpen, setIsOpen] = useState(false);
    const isEffectivelyOpen = isOpen || shouldForceExpand;

    if (!matchesSearch(entity, searchTerm)) return null;

    const sorted = sortBy(entity.children ?? [], c => (c.entityType === "Directory" ? `A${c.name}` : c.name));

    return (
        <>
            <Flex
                align="center"
                gap={1}
                pl={`${depth * 1.25 + 0.5}rem`}
                pr={2}
                py={1.5}
                cursor="pointer"
                borderRadius="sm"
                bg={isSelected ? "blue.50" : undefined}
                color={isSelected ? "blue.700" : undefined}
                fontWeight={isSelected ? "semibold" : undefined}
                borderLeft={isSelected ? "3px solid" : "3px solid transparent"}
                borderColor={isSelected ? "blue.400" : "transparent"}
                _hover={{ bg: isSelected ? "blue.50" : "gray.50" }}
                onClick={() => {
                    if (isDirectory && !searchTerm) setIsOpen(o => !o);
                    else onSelect(entity.id);
                }}
            >
                {/* Expand/collapse chevron for directories */}
                <Box w="1rem" flexShrink={0} color="gray.400">
                    {hasChildren && !searchTerm && (
                        <Icon
                            as={isEffectivelyOpen ? IconChevronDown : IconChevronRight}
                            boxSize="0.8rem"
                            onClick={e => {
                                e.stopPropagation();
                                setIsOpen(o => !o);
                            }}
                        />
                    )}
                </Box>
                <Icon as={isDirectory ? IconFolder : IconBuilding} boxSize="0.9rem" flexShrink={0} color={isSelected ? "blue.500" : "gray.500"} />
                <Text fontSize="sm" flex={1} noOfLines={1} userSelect="none">
                    {entity.name}
                </Text>
                {!isDirectory && entity.candidateCount > 0 && (
                    <Badge colorScheme="blue" borderRadius="full" fontSize="2xs" flexShrink={0}>
                        {entity.candidateCount}
                    </Badge>
                )}
            </Flex>

            {hasChildren &&
                isEffectivelyOpen &&
                sorted.map(child => (
                    <TreeItem key={child.id} entity={child} searchTerm={searchTerm} depth={depth + 1} selectedId={selectedId} onSelect={onSelect} />
                ))}
        </>
    );
}

export function EntityScopePicker({ entities, selectedId, isLoading, onChange }: EntityScopePickerProps) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [searchTerm, setSearchTerm] = useState("");
    const searchRef = useRef<HTMLInputElement>(null);

    const selectedEntity = useMemo(() => (selectedId && entities ? findEntityById(entities, selectedId) : undefined), [entities, selectedId]);

    const sorted = useMemo(() => sortBy(entities ?? [], e => (e.entityType === "Directory" ? `A${e.name}` : e.name)), [entities]);

    function handleSelect(id: string) {
        onChange(id);
        setSearchTerm("");
        onClose();
    }

    function handleClear(e: React.MouseEvent) {
        e.stopPropagation();
        onChange(undefined);
    }

    function handleOpen() {
        onOpen();
        setTimeout(() => searchRef.current?.focus(), 50);
    }

    return (
        <Popover
            isOpen={isOpen}
            onClose={() => {
                onClose();
                setSearchTerm("");
            }}
            placement="bottom-start"
            isLazy
        >
            <PopoverTrigger>
                {selectedEntity ? (
                    <Button
                        size="sm"
                        variant="outline"
                        colorScheme="blue"
                        leftIcon={<Icon as={IconSitemap} boxSize="0.9rem" />}
                        rightIcon={<CloseButton size="sm" onClick={handleClear} />}
                        onClick={handleOpen}
                        pr={1}
                        maxW="220px"
                    >
                        <Text noOfLines={1} flex={1} textAlign="left">
                            {selectedEntity.name}
                        </Text>
                    </Button>
                ) : (
                    <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<Icon as={IconSitemap} boxSize="0.9rem" />}
                        rightIcon={<Icon as={IconChevronDown} boxSize="0.8rem" />}
                        onClick={handleOpen}
                        color="gray.600"
                    >
                        All entities
                    </Button>
                )}
            </PopoverTrigger>

            <PopoverContent w="280px" shadow="lg">
                <PopoverBody p={0}>
                    <Box px={2} pt={2} pb={1} borderBottomWidth={1}>
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={IconSearch} boxSize="0.85rem" color="gray.400" />
                            </InputLeftElement>
                            <Input
                                ref={searchRef}
                                placeholder="Search entities…"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                border="none"
                                _focus={{ boxShadow: "none" }}
                            />
                        </InputGroup>
                    </Box>

                    <Box maxH="320px" overflowY="auto" py={1}>
                        {/* All entities option */}
                        {!searchTerm && (
                            <HStack
                                px={2}
                                py={1.5}
                                cursor="pointer"
                                borderRadius="sm"
                                bg={!selectedId ? "blue.50" : undefined}
                                color={!selectedId ? "blue.700" : "gray.600"}
                                fontWeight={!selectedId ? "semibold" : undefined}
                                borderLeft={!selectedId ? "3px solid" : "3px solid transparent"}
                                borderColor={!selectedId ? "blue.400" : "transparent"}
                                _hover={{ bg: !selectedId ? "blue.50" : "gray.50" }}
                                mx={1}
                                onClick={() => {
                                    onChange(undefined);
                                    setSearchTerm("");
                                    onClose();
                                }}
                            >
                                <Box w="1rem" flexShrink={0} />
                                <Icon as={IconSitemap} boxSize="0.9rem" color={!selectedId ? "blue.500" : "gray.400"} />
                                <Text fontSize="sm" userSelect="none">
                                    All entities
                                </Text>
                            </HStack>
                        )}

                        {isLoading && (
                            <Flex justify="center" py={4}>
                                <Spinner size="sm" />
                            </Flex>
                        )}

                        {!isLoading &&
                            sorted.map(entity => (
                                <TreeItem key={entity.id} entity={entity} searchTerm={searchTerm} depth={0} selectedId={selectedId} onSelect={handleSelect} />
                            ))}

                        {!isLoading && sorted.length === 0 && (
                            <Text fontSize="sm" color="gray.500" px={3} py={2}>
                                No entities found
                            </Text>
                        )}
                    </Box>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
